import type {
	KybFormDataAboutSchema,
	KybFormDataAcknowledgementSchema,
	KybFormDirectorsSchema,
	kybFormStages,
} from 'BreetComponents';
import { emailValidation, type IKybDirector, type IUseFormData, type KybStatusType } from 'BreetHelpers';
import type { Ref } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import * as yup from 'yup';

export const acknowledgementItems = [
	'I am authorized to act on behalf of the company named in this application.',
	'All the information provided in this form is accurate and complete to the best of my knowledge.',
	'I understand that providing false or misleading information may result in the suspension or termination of services.',
	'I acknowledge that Breet Technologies Limited may request additional documentation or clarification to comply with regulatory and due diligence requirements.',
	'I agree that the company I represent is not involved in any activity related to money laundering, terrorism financing, or any other illegal financial operations.',
	'I understand that Breet is required to comply with applicable Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF) laws and may report suspicious activity to relevant authorities.',
	'I consent to Breet conducting identity verification, background checks, and transaction monitoring as part of its AML compliance program.',
	'I agree to cooperate fully with Breet in the event of any compliance review, audit, or investigation.',
	"I agree to comply with Breet's Terms of Service, Privacy Policy, and all applicable laws and regulations.",
];

export const KybFormDataAuthEmailSchema = yup.object({
	officialEmail: emailValidation,
});

export type KybFormDataAuthEmailSchemaType = yup.InferType<typeof KybFormDataAuthEmailSchema>;

export const KybFormDataAuthOTPSchema = yup.object({
	otp: yup
		.string()
		.required('OTP is required')
		.trim()
		.matches(/^\d+$/, 'OTP must contain only numbers')
		.length(4, 'OTP must be exactly 4 digits'),
});

export type KybFormDataAuthOTPSchemaType = yup.InferType<typeof KybFormDataAuthOTPSchema>;

export type KybFormDirectorsSchemaType = yup.InferType<typeof KybFormDirectorsSchema>;
export type KybFormAboutSchemaType = yup.InferType<typeof KybFormDataAboutSchema>;
export type KybFormAcknowledgementSchemaType = yup.InferType<typeof KybFormDataAcknowledgementSchema>;

export type KybFormStageTitle = (typeof kybFormStages)[number]['title'];

export const kybAboutDefaultFormData: KybFormAboutSchemaType = {
	companyName: '',
	officialEmail: '',
	country: '',
	officialTelephone: '',
	taxId: '',
	evidenceOfRegistration: '',
	proofOfAddress: '',
	memart: '',
	statusReport: '',
	extraDoc: '',
	businessDescription: '',
};

export const kybDefaultDirectorsData: IKybDirector = {
	firstName: '',
	fullHomeAddress: '',
	lastName: '',
	meansOfID: '',
	recentAddressProof: '',
};

export type LimitedKybStatusType = Extract<KybStatusType, 'completed' | 'pending'>;

export const kybFormStatus: Record<LimitedKybStatusType, { subTitle: string; title: string }> = {
	completed: {
		subTitle: "We are now reviewing it and we'll be in touch soon!",
		title: 'Thank you for your submission',
	},
	pending: {
		subTitle: 'Your KYB request has already been submitted and is currently under review.',
		title: 'Under Review',
	},
};

export interface CTASingleProps {
	saveClick?: () => void;
	buttonDisabled: boolean;
	isLoading?: boolean;
}

export interface CTATupleinterfaceProps {
	prevClick?: () => void;
	saveClick?: () => void;
	buttonDisabled: boolean;
	loading?: boolean;
	saveTitle?: string;
}

export interface IKybAcknowledgementItems {
	register?: UseFormRegisterReturn;
	errorMessage?: string;
}

export interface IKybFormHeader {
	title: string;
	subTitle: string;
}

export interface IKybDirectorsFormItem {
	index: number;
	formDataContextProps: IUseFormData<typeof KybFormDirectorsSchema>;
	deleteFormItem: () => void;
	isSingleFormItem: boolean;
	containerRef: Ref<HTMLDivElement> | undefined;
}

export interface IKybDirectorsFormItemComponents {
	formDataContextProps: IUseFormData<typeof KybFormDirectorsSchema>;
	index: number;
}

export const fileFields = new Set(['evidenceOfRegistration', 'proofOfAddress', 'memart', 'statusReport', 'extraDoc']);
